import { useState } from "react";
import Control from "../../common/Control";
import vehicleTools from "../../../helpers/vehicleTools";
import styles from "./QuotationLine.module.scss";

// Taux appliqué par le bouton "marge = % du loyer financier" en un clic
const MARGIN_FINANCE_RENT_RATE_DEFAULT = 23;

const QuotationTable = ({
  quotation,
  rlChange,
  rlVehicleChange,
  doesToggleProtectedFields,
  constantData,
  insuranceHTCalc,
  insuranceFinLossCalc,
  totalRentHTCalc,
  totalRentTTCCalc,
  pricePublicTTC,
  priceNetTTC,
  investAmtHT,
  totalBenefits,
  shouldHideRow,
  updateLocks,
  locks,
  vehicle,
  TVA_INVERT,
  state,
  baseRentHT,
  applyMarginFromFinanceRent,
}) => {
  const allContractsGenerated =
    quotation.contracts && quotation.contracts.length >= quotation.quantity;

  // null = l'input affiche la valeur réelle (marge courante en % du loyer
  // financier) ; une string = saisie en cours, à confirmer
  const [financeRentMarginRateInput, setFinanceRentMarginRateInput] =
    useState(null);
  const financeRentCalc = vehicleTools.calcFinanceRent(quotation, TVA_INVERT);
  const marginPctOfFinanceRent =
    financeRentCalc > 0
      ? Math.round(
          ((parseFloat(quotation.marginTTC) || 0) / financeRentCalc) * 1000,
        ) / 10
      : null;
  const displayedFinanceRentMarginRate =
    financeRentMarginRateInput !== null
      ? financeRentMarginRateInput
      : marginPctOfFinanceRent !== null
        ? marginPctOfFinanceRent
        : "";
  const financeRentMarginRateNum = parseFloat(financeRentMarginRateInput);
  const canConfirmFinanceRentMargin =
    financeRentMarginRateInput !== null &&
    financeRentCalc > 0 &&
    !isNaN(financeRentMarginRateNum) &&
    financeRentMarginRateNum > 0 &&
    financeRentMarginRateNum <= 100;

  const applyFinanceRentMargin = (rate) => {
    const marginTTC = Math.round(((financeRentCalc * rate) / 100) * 100) / 100;
    applyMarginFromFinanceRent(marginTTC);
    setFinanceRentMarginRateInput(null);
  };

  return (
    <div className={styles.band}>
      <div className={`${styles.cell} ${styles.cellQty}`}>
        <div className={styles.cellLabel}>Qté</div>
        <Control
          type="number"
          name="quantity"
          value={quotation.quantity}
          change={rlChange}
        />
      </div>

      <div className={`${styles.cell} ${styles.cellWide}`}>
        <div className={styles.cellLabel} title="Prix public TTC (hors CG/BM)">
          Prix pub. TTC
        </div>
        <div
          className={styles.cellValue}
          title={`${Math.round(pricePublicTTC * 100) / 100} €`}
        >
          {Math.round(pricePublicTTC * 100) / 100} €
        </div>
        <div
          className={
            quotation.vehicle.malusBonusEco > 0
              ? styles.subRed
              : styles.subGreen
          }
        >
          {quotation.vehicle.malusBonusEco > 0 ? "Malus : " : "Bonus : "}
          {quotation.vehicle.malusBonusEco
            ? Math.abs(quotation.vehicle.malusBonusEco)
            : 0}{" "}
          €
        </div>
        {vehicle.optionsNotIncluded.length > 0 && (
          <div className={styles.subGray}>
            Options :{" "}
            {Math.round(
              vehicle.optionsNotIncluded.reduce(
                (a, b) => a + b.priceHT * (b.isInclTVA ? TVA_INVERT : 1),
                0,
              ) * 100,
            ) / 100}
            €
          </div>
        )}
      </div>

      <div className={styles.cell}>
        <div className={styles.cellLabel} title="Remise sur prix TTC">
          Remise TTC
        </div>
        <div className="input-group">
          <input
            className="form-control"
            type="text"
            name="discountPct"
            value={vehicle.discountPct}
            onChange={rlVehicleChange}
          />
          <div className="input-group-append">
            <span className="input-group-text">%</span>
          </div>
        </div>
        <div className="input-group">
          <input
            className="form-control"
            type="text"
            name="discount"
            value={vehicle.discount}
            onChange={rlVehicleChange}
          />
          <div className="input-group-append">
            <span className="input-group-text">€</span>
          </div>
        </div>
      </div>

      <div className={styles.cell}>
        <div className={styles.cellLabel}>Prix net TTC</div>
        <div
          className={styles.cellValue}
          title={`${priceNetTTC ? Math.round(priceNetTTC * 100) / 100 : 0} €`}
        >
          {priceNetTTC ? Math.round(priceNetTTC * 100) / 100 : 0} €
        </div>
      </div>

      <div className={styles.cell}>
        <div className={styles.cellLabel} title="Montant invest HT">
          Invest HT
        </div>
        <div className={styles.cellValue} title={`${investAmtHT} €`}>
          {investAmtHT} €
        </div>
      </div>

      <div className={styles.cell} data-priv="invisible-commerciaux">
        <div className={styles.cellLabel}>Marge TTC</div>
        <Control
          name="marginTTCRate"
          value={quotation.marginTTCRate}
          type="number"
          change={rlChange}
          error={state.error}
          defferChange={true}
          suffix={"%"}
        />
        <Control
          name="marginTTC"
          value={quotation.marginTTC}
          type="number"
          change={rlChange}
          error={state.error}
          defferChange={true}
          suffix={"€"}
        />
        <div
          className="input-group"
          title="Marge courante en % du loyer financier calculé — modifier la valeur puis confirmer"
        >
          <input
            className="form-control"
            type="number"
            value={displayedFinanceRentMarginRate}
            onChange={(e) => setFinanceRentMarginRateInput(e.target.value)}
            disabled={financeRentCalc <= 0}
            aria-label="Marge en % du loyer financier"
          />
          <div className="input-group-append">
            <span className="input-group-text" title="% du loyer financier">
              % l.f.
            </span>
            <button
              type="button"
              className="btn btn-primary btn-sm px-2"
              disabled={!canConfirmFinanceRentMargin}
              onClick={() => applyFinanceRentMargin(financeRentMarginRateNum)}
              title="Confirmer ce % du loyer financier comme marge"
              aria-label="Confirmer ce % du loyer financier comme marge"
            >
              <i className="fa fa-check" />
            </button>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-default shadow-none btn-sm btn-block mt-1"
          style={{ fontSize: "0.7rem", textTransform: "none" }}
          disabled={financeRentCalc <= 0}
          onClick={() =>
            applyFinanceRentMargin(MARGIN_FINANCE_RENT_RATE_DEFAULT)
          }
          title={`Remplace la marge actuelle par ${MARGIN_FINANCE_RENT_RATE_DEFAULT}% du loyer financier calculé (montant € et taux % recalculés)`}
        >
          Appl. {MARGIN_FINANCE_RENT_RATE_DEFAULT}% loy.fin
        </button>
      </div>

      <div className={styles.cell}>
        <div className={styles.cellLabel} title="Loyer de base HT">
          Loyer base HT
        </div>
        <div className={styles.cellValue} title={`${baseRentHT} €`}>
          {baseRentHT} €
        </div>
      </div>

      <div className={styles.cell}>
        <div className={styles.cellLabel} title="Véhicule relais">
          Vhc relais
          {doesToggleProtectedFields && (
            <input
              type="checkbox"
              checked={quotation.replacementVehicleCost > 0}
              onChange={(e) =>
                rlChange({
                  target: {
                    name: "replacementVehicleCost",
                    value: !e.target.checked
                      ? 0
                      : parseFloat(
                          constantData.REPLACEMENT_VEHICULE_COST_DEFAULT,
                        ),
                  },
                })
              }
            />
          )}
        </div>
        <Control
          name="replacementVehicleCost"
          suffix={"€"}
          change={rlChange}
          value={quotation.replacementVehicleCost}
          disabled={doesToggleProtectedFields}
        />
      </div>

      <div className={styles.cell}>
        <div className={styles.cellLabel} title="Assurance Prévoyance">
          Prévoyance
          {doesToggleProtectedFields && (
            <input
              type="checkbox"
              checked={quotation.insuranceHT > 0}
              onChange={(e) =>
                rlChange({
                  target: {
                    name: "insuranceHT",
                    value: !e.target.checked ? 0 : insuranceHTCalc,
                  },
                })
              }
            />
          )}
          {locks.insuranceHT ? (
            <i
              title="Déverrouiller cette valeur pour permettre son calcul automatique"
              className={`fa fa-lock ${styles.lockIcon} ${styles.lockClosed}`}
              onClick={() => updateLocks("insuranceHT")}
            ></i>
          ) : (
            <i
              title="Verrouiller cette valeur pour empêcher son calcul automatique"
              className={`fa fa-lock-open ${styles.lockIcon} ${styles.lockOpen}`}
              onClick={() => updateLocks("insuranceHT")}
            ></i>
          )}
        </div>
        <Control
          name="insuranceHT"
          value={quotation.insuranceHT}
          change={rlChange}
          suffix="€"
          disabled={doesToggleProtectedFields}
        />
      </div>

      <div className={styles.cell}>
        <div className={styles.cellLabel} title="Option Sérénité">
          Sérénité
          {doesToggleProtectedFields && (
            <input
              type="checkbox"
              checked={quotation.insuranceFinLoss > 0}
              onChange={(e) =>
                rlChange({
                  target: {
                    name: "insuranceFinLoss",
                    value: !e.target.checked ? 0 : insuranceFinLossCalc,
                  },
                })
              }
            />
          )}
          {locks.insuranceFinLoss ? (
            <i
              title="Déverrouiller cette valeur pour permettre son calcul automatique"
              className={`fa fa-lock ${styles.lockIcon} ${styles.lockClosed}`}
              onClick={() => updateLocks("insuranceFinLoss")}
            ></i>
          ) : (
            <i
              title="Verrouiller cette valeur pour empêcher son calcul automatique"
              className={`fa fa-lock-open ${styles.lockIcon} ${styles.lockOpen}`}
              onClick={() => updateLocks("insuranceFinLoss")}
            ></i>
          )}
        </div>
        <Control
          name="insuranceFinLoss"
          value={quotation.insuranceFinLoss}
          change={rlChange}
          suffix="€"
          disabled={doesToggleProtectedFields}
        />
      </div>

      <div className={styles.cell}>
        <div className={styles.cellLabel} title="Autres prestations">
          Prestations
        </div>
        <div className={styles.cellValueLight} title={`${totalBenefits} €`}>
          {totalBenefits} €
        </div>
      </div>

      <div className={styles.totals}>
        <div className={styles.cell}>
          <div className={styles.cellLabel}>Total loyer HT</div>
          <div className={styles.totalValue} title={`${totalRentHTCalc} €`}>
            {totalRentHTCalc} €
          </div>
        </div>
        <div className={styles.cell}>
          <div className={styles.cellLabel}>Total loyer TTC</div>
          <div className={styles.totalValue} title={`${totalRentTTCCalc} €`}>
            {totalRentTTCCalc} €
          </div>
        </div>
      </div>

      {(shouldHideRow || allContractsGenerated) && (
        <div className={styles.backdrop}>
          {allContractsGenerated && (
            <span>Tous les contrats ont été générés</span>
          )}
        </div>
      )}
    </div>
  );
};

export default QuotationTable;
